/**
 * NOS-108: Public site nav bar with mobile menu (hamburger + drawer).
 * NOS-188: one level of dropdown children (org_navigation.parent_id) —
 * hover/click dropdowns on desktop, nested indented links in the mobile drawer.
 * Used by PublicWebsitePage and InlinePreview.
 * - Desktop (≥768px): horizontal nav links
 * - Mobile (<768px): hamburger, drawer with stacked links + CTA
 *
 * Best practice: a "Donate" CTA is always shown in the nav for nonprofits.
 * It opens the DonationModal when orgId is provided (public site).
 * In InlinePreview (no orgId), the button is display-only.
 * To hide: set navCtaText to empty string in org customizations.
 *
 * NOS-215: an optional SECONDARY CTA (outline button, e.g. an imported
 * "Subscribe") renders before the primary. It is stored as a self-parented
 * org_navigation row (see findSecondaryNavCta) so it arrives through the
 * regular navItems prop; fragment hrefs scroll to their anchor (or page end).
 */
import { useState, useEffect, useMemo } from "react"
import { Link, useLocation } from "react-router-dom"
import { Menu, X, ChevronDown } from "lucide-react"
import { buildNavTree, findSecondaryNavCta, type NavItem } from "@/types/navigation"
import { DonationModal } from "@/components/DonationModal"
import { AnnouncementBar } from "@/components/AnnouncementBar"
import type { AnnouncementConfig } from "@/lib/announcement"

/** Grouping-only dropdown parents (non-link nav entries) are stored with href "#". */
function isGroupingHref(href: string): boolean {
  return href === "#"
}

const MOBILE_BREAKPOINT = 768

function pathWithoutQuery(path: string): string {
  return path.split("?")[0] || "/"
}

function normalizeTrailing(path: string): string {
  const p = pathWithoutQuery(path)
  if (p.length > 1 && p.endsWith("/")) return p.slice(0, -1)
  return p || "/"
}

/** Active when pathname matches link, or for /blog and /events prefixes (posts, detail). */
function isNavLinkActive(pathname: string, resolvedPath: string, itemHref: string): boolean {
  const p = normalizeTrailing(pathname)
  const target = normalizeTrailing(resolvedPath)
  if (p === target) return true
  const slug = itemHref.replace(/^\//, "").split("?")[0].toLowerCase()
  if (slug === "blog" && (p === target || p.startsWith(`${target}/`))) return true
  if (slug === "events" && (p === target || p.startsWith(`${target}/`))) return true
  if (slug === "campaign" && (p === target || p.startsWith(`${target}/`))) return true
  return false
}

export interface PublicNavBarProps {
  orgName: string
  logoUrl?: string | null
  navItems: NavItem[]
  pages: Array<{ id: string; slug: string; title: string | null }>
  primaryColor: string
  navCtaText: string
  fontFamily: string
  /** Returns React Router path for internal page links. When absent, use hash #slug (InlinePreview) */
  getPagePath?: (slug: string) => string
  /** For InlinePreview: when canvas is mobile viewport, force hamburger */
  isMobileViewport?: boolean
  /** For InlinePreview: wrapper is clickable for section selection */
  selectable?: boolean
  selectedSectionId?: string | null
  onSectionClick?: (sectionId: string, e: React.MouseEvent) => void
  /** When provided, the CTA button opens the DonationModal. Omit in InlinePreview. */
  orgId?: string
  /** Logo scale multiplier (default 1.0). Applied to the base nav logo height of 32px. */
  logoScale?: number
  /** Whether the nav sticks to the top on scroll (default true). Ignored in builder canvas. */
  stickyNav?: boolean
  /** NOS-239: site-wide announcement bar rendered above the nav (chrome). */
  announcement?: AnnouncementConfig | null
}

function useIsMobile(forceMobile?: boolean) {
  const [isMobile, setIsMobile] = useState(forceMobile ?? false)

  useEffect(() => {
    if (forceMobile) {
      setIsMobile(true)
      return
    }
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    setIsMobile(mq.matches)
    const handler = () => setIsMobile(mq.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [forceMobile])

  return isMobile
}

export function PublicNavBar({
  orgName,
  logoUrl,
  navItems,
  pages,
  primaryColor,
  navCtaText,
  fontFamily,
  getPagePath,
  isMobileViewport = false,
  selectable = false,
  selectedSectionId = null,
  onSectionClick,
  orgId,
  logoScale = 1.0,
  stickyNav = true,
  announcement = null,
}: PublicNavBarProps) {
  const { pathname } = useLocation()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [donationModalOpen, setDonationModalOpen] = useState(false)
  // NOS-188: which top-level item's dropdown is open (desktop)
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  const isMobile = useIsMobile(isMobileViewport)
  const showCta = navCtaText.trim().length > 0

  const closeDrawer = () => setDrawerOpen(false)

  const homePath = getPagePath?.("home")

  /**
   * NOS-261: legacy fallback — orgs with no org_navigation rows render their
   * published pages as nav links. Normalizing pages → NavItem[] here lets the
   * tree and legacy branches share one render path. Without getPagePath
   * (InlinePreview) the href is a #slug anchor, preserving builder-preview
   * navigation.
   */
  const effectiveNavItems = useMemo(() => {
    if (navItems.length > 0) return navItems
    return pages.map(
      (p, idx): NavItem => ({
        id: p.id,
        org_id: orgId ?? "",
        label: p.title ?? p.slug ?? "Untitled",
        href: getPagePath ? `/${p.slug.replace(/^\//, "")}` : `#${p.slug}`,
        order_index: idx,
        open_in_new_tab: false,
        parent_id: null,
        created_at: "",
        updated_at: "",
      })
    )
  }, [navItems, pages, orgId, getPagePath])

  const navTree = useMemo(() => buildNavTree(effectiveNavItems), [effectiveNavItems])
  // NOS-215: self-parented row = secondary CTA (outline). Excluded from navTree.
  const secondaryCta = useMemo(() => findSecondaryNavCta(navItems), [navItems])

  /** Resolve a nav item to a router path (internal) or raw href, plus active state. */
  const resolveNav = (item: NavItem) => {
    const isInternal = item.href.startsWith("/") && !item.href.startsWith("//")
    const pageSlug = isInternal ? item.href.replace(/^\//, "").split("?")[0] || "home" : null
    const path = pageSlug && getPagePath ? getPagePath(pageSlug) : null
    const active = path ? isNavLinkActive(pathname, path, item.href) : false
    return { path, href: path ?? item.href, active }
  }

  /** Shared desktop link style (NOS-261: single-sourced, was copy-pasted per branch). */
  const desktopLinkStyle = (active: boolean): React.CSSProperties => ({
    fontSize: 14,
    color: active ? primaryColor : "#666",
    textDecoration: "none",
    fontFamily,
    fontWeight: active ? 600 : 400,
    borderBottom: active ? `2px solid ${primaryColor}` : "2px solid transparent",
    paddingBottom: 2,
    // NOS-234: mirror the underline reservation on top so the box is
    // symmetric around the text — otherwise align-items centering puts
    // link text 2px above the CTA buttons' (symmetric-padding) text.
    borderTop: "2px solid transparent",
    paddingTop: 2,
    // Plain links and dropdown triggers must produce identical boxes,
    // or the parent's align-items centering shows font-metric drift
    // (inline line box vs content-sized flex box).
    display: "inline-flex",
    alignItems: "center",
  })

  const dropdownChildStyle = (active: boolean): React.CSSProperties => ({
    display: "block",
    padding: "8px 14px",
    fontSize: 13,
    color: active ? primaryColor : "#444",
    fontWeight: active ? 600 : 400,
    textDecoration: "none",
    fontFamily,
    whiteSpace: "nowrap",
  })

  const drawerLinkStyle = (
    active: boolean,
    isChild: boolean,
    isLast: boolean
  ): React.CSSProperties => ({
    display: "block",
    padding: "14px 0",
    paddingLeft: isChild ? 18 : 0,
    fontSize: isChild ? 14 : 15,
    fontWeight: active ? 600 : 500,
    color: active ? primaryColor : isChild ? "#555" : "#333",
    textDecoration: "none",
    fontFamily,
    borderBottom: isLast ? "none" : "1px solid #eee",
  })

  /**
   * NOS-261: the one nav link renderer — router Link when the item resolves to
   * an internal path, plain <a> otherwise. target="_blank" ONLY when
   * open_in_new_tab (B9: the drawer previously hardcoded it on every <a>).
   */
  const renderNavLink = (item: NavItem, style: React.CSSProperties, onClick?: () => void) => {
    const { path, href } = resolveNav(item)
    if (item.open_in_new_tab || !path) {
      return (
        <a
          key={item.id}
          href={href}
          target={item.open_in_new_tab ? "_blank" : undefined}
          rel={item.open_in_new_tab ? "noopener noreferrer" : undefined}
          style={style}
          onClick={onClick}
        >
          {item.label}
        </a>
      )
    }
    return (
      <Link key={item.id} to={path} style={style} onClick={onClick}>
        {item.label}
      </Link>
    )
  }

  /**
   * NOS-215: fragment CTA — scroll to the anchor; NOS-232: when the source
   * anchor id doesn't exist (imported "#footer-email-optin" etc.), prefer the
   * footer subscribe block over the bare page end so Subscribe lands on a
   * working email capture.
   */
  const scrollToFragment = (hash: string) => {
    const el =
      document.getElementById(hash.replace(/^#/, "")) ??
      document.querySelector("[data-footer-subscribe]")
    if (el) el.scrollIntoView({ behavior: "smooth" })
    else window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" })
  }

  /** NOS-215: outline-variant secondary CTA for the desktop bar or mobile drawer. */
  const renderSecondaryCta = (variant: "bar" | "drawer") => {
    if (!secondaryCta) return null
    const style: React.CSSProperties =
      variant === "bar"
        ? {
            fontSize: 13,
            fontWeight: 600,
            color: primaryColor,
            background: "transparent",
            padding: "4px 11px",
            borderRadius: 5,
            border: `1.5px solid ${primaryColor}`,
            textDecoration: "none",
            fontFamily,
            cursor: "pointer",
          }
        : {
            display: "block",
            width: "100%",
            fontSize: 15,
            fontWeight: 700,
            color: primaryColor,
            background: "transparent",
            padding: "12px 24px",
            border: `1.5px solid ${primaryColor}`,
            borderRadius: 8,
            textDecoration: "none",
            textAlign: "center",
            fontFamily,
            letterSpacing: "0.02em",
            cursor: "pointer",
            boxSizing: "border-box",
            marginBottom: 10,
          }
    // InlinePreview (no orgId): display-only, like the primary CTA
    if (!orgId) {
      return <span style={style}>{secondaryCta.label}</span>
    }
    if (secondaryCta.href.startsWith("#")) {
      return (
        <button
          type="button"
          onClick={() => {
            closeDrawer()
            scrollToFragment(secondaryCta.href)
          }}
          style={style}
        >
          {secondaryCta.label}
        </button>
      )
    }
    return renderNavLink(secondaryCta, style, closeDrawer)
  }

  const brandContent = logoUrl ? (
    <img
      src={logoUrl}
      alt={orgName}
      style={{ height: Math.round(64 * logoScale * (isMobile ? 0.75 : 1)), width: "auto", maxWidth: isMobile ? 210 : 280, objectFit: "contain", display: "block" }}
    />
  ) : (
    <span style={{ fontSize: 14, fontWeight: 700, color: "#0f0f14", fontFamily }}>
      {orgName}
    </span>
  )

  const navBarContent = (
    <>
      {homePath ? (
        <Link
          to={homePath}
          style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
          onClick={closeDrawer}
        >
          {brandContent}
        </Link>
      ) : (
        <div style={{ display: "flex", alignItems: "center" }}>
          {brandContent}
        </div>
      )}

      {isMobile ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setDrawerOpen((o) => !o)
          }}
          aria-label={drawerOpen ? "Close menu" : "Open menu"}
          style={{
            background: "transparent",
            border: "none",
            minWidth: 44,
            minHeight: 44,
            padding: 10,
            cursor: "pointer",
            color: "#0f0f14",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {drawerOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 16, fontFamily }}>
          {navTree.map((node) => {
            const { path, href, active: selfActive } = resolveNav(node)
            const childStates = node.children.map((c) => ({
              item: c,
              active: resolveNav(c).active,
            }))
            const active = selfActive || childStates.some((c) => c.active)
            const linkStyle = desktopLinkStyle(active)

            // Plain item — no dropdown (existing behavior)
            if (node.children.length === 0) {
              return renderNavLink(node, linkStyle)
            }

            // NOS-188: dropdown parent — hover opens the children menu;
            // grouping-only parents (href "#") toggle on click instead of navigating.
            const open = openDropdownId === node.id
            const grouping = isGroupingHref(node.href)
            const triggerStyle = {
              ...linkStyle,
              display: "inline-flex" as const,
              alignItems: "center" as const,
              gap: 4,
            }
            const triggerInner = (
              <>
                {node.label}
                <ChevronDown
                  size={12}
                  style={{ transition: "transform 0.15s", transform: open ? "rotate(180deg)" : "none" }}
                />
              </>
            )
            return (
              <div
                key={node.id}
                style={{ position: "relative", display: "flex" }}
                onMouseEnter={() => setOpenDropdownId(node.id)}
                onMouseLeave={() => setOpenDropdownId((cur) => (cur === node.id ? null : cur))}
              >
                {grouping || (!path && !node.open_in_new_tab) ? (
                  <button
                    type="button"
                    aria-haspopup="true"
                    aria-expanded={open}
                    onClick={() => setOpenDropdownId(open ? null : node.id)}
                    style={{
                      ...triggerStyle,
                      background: "transparent",
                      // Per-side only: `border`/`padding` shorthands would land AFTER the
                      // spread's borderBottom/paddingBottom in object key order (spread keeps
                      // a duplicate key's original position) and wipe the reserved 2px+2px,
                      // dropping button triggers 2px below plain links. borderTop/paddingTop
                      // stay inherited from linkStyle (NOS-234 symmetric box).
                      borderRight: "none",
                      borderLeft: "none",
                      paddingRight: 0,
                      paddingLeft: 0,
                      cursor: "pointer",
                    }}
                  >
                    {triggerInner}
                  </button>
                ) : node.open_in_new_tab || !path ? (
                  <a
                    href={href}
                    target={node.open_in_new_tab ? "_blank" : undefined}
                    rel={node.open_in_new_tab ? "noopener noreferrer" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open}
                    style={triggerStyle}
                  >
                    {triggerInner}
                  </a>
                ) : (
                  <Link to={path} aria-haspopup="true" aria-expanded={open} style={triggerStyle}>
                    {triggerInner}
                  </Link>
                )}
                {open && (
                  <div style={{ position: "absolute", top: "100%", left: 0, paddingTop: 8, zIndex: 60 }}>
                    <div
                      role="menu"
                      style={{
                        background: "#fff",
                        border: "1px solid #eee",
                        borderRadius: 8,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        padding: "6px 0",
                        minWidth: 180,
                      }}
                    >
                      {childStates.map(({ item: child, active: childActive }) =>
                        renderNavLink(child, dropdownChildStyle(childActive), () =>
                          setOpenDropdownId(null)
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
          {renderSecondaryCta("bar")}
          {showCta && (
            orgId ? (
              <button
                type="button"
                onClick={() => setDonationModalOpen(true)}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#fff",
                  background: primaryColor,
                  padding: "5px 12px",
                  borderRadius: 5,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {navCtaText}
              </button>
            ) : (
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#fff",
                  background: primaryColor,
                  padding: "5px 12px",
                  borderRadius: 5,
                }}
              >
                {navCtaText}
              </span>
            )
          )}
        </div>
      )}
    </>
  )

  // NOS-188: flatten the tree for the drawer — parents followed by their
  // (indented) children, matching the desktop dropdown structure.
  const drawerEntries = navTree.flatMap((node) => [
    { item: node as NavItem, isChild: false, hasChildren: node.children.length > 0 },
    ...node.children.map((c) => ({ item: c, isChild: true, hasChildren: false })),
  ])

  const drawerLinks = drawerEntries.map(({ item, isChild, hasChildren }, idx) => {
    const { active } = resolveNav(item)
    const isLast = idx === drawerEntries.length - 1
    const linkStyle = drawerLinkStyle(active, isChild, isLast)
    // Grouping-only parents (href "#") are section labels, not links
    if (!isChild && hasChildren && isGroupingHref(item.href)) {
      return (
        <div key={item.id} style={{ ...linkStyle, color: "#888", borderBottom: "none", paddingBottom: 4 }}>
          {item.label}
        </div>
      )
    }
    return renderNavLink(item, linkStyle, closeDrawer)
  })

  const drawerContent = (
    <div
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        background: "#fff",
        borderBottom: "1px solid #eee",
        zIndex: 50,
        overflow: "hidden",
        overflowY: drawerOpen ? "auto" : "hidden",
        transition: "max-height 0.2s ease-out",
        // NOS-188: nested children can exceed the old 400px cap
        maxHeight: drawerOpen ? "70vh" : 0,
      }}
    >
      <div style={{ padding: "14px 24px 1px" }}>
        {drawerLinks}
        {secondaryCta && <div style={{ marginTop: 12 }}>{renderSecondaryCta("drawer")}</div>}
        {showCta && (
          <div style={{ margin: "0 -24px 0" }}>
            {orgId ? (
              <button
                type="button"
                onClick={() => { closeDrawer(); setDonationModalOpen(true) }}
                style={{
                  display: "block",
                  width: "100%",
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#fff",
                  background: primaryColor,
                  padding: "14px 24px",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "center",
                  fontFamily,
                  letterSpacing: "0.02em",
                }}
              >
                {navCtaText}
              </button>
            ) : (
              <a
                href="#donate"
                onClick={closeDrawer}
                style={{
                  display: "block",
                  width: "100%",
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#fff",
                  background: primaryColor,
                  padding: "14px 24px",
                  textDecoration: "none",
                  textAlign: "center",
                  fontFamily,
                  letterSpacing: "0.02em",
                  boxSizing: "border-box",
                }}
              >
                {navCtaText}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <>
      <div style={selectable || !stickyNav ? { position: "relative" } : { position: "sticky", top: 0, zIndex: 50 }}>
        {/* NOS-239: announcement bar — chrome above the nav, site-wide */}
        <AnnouncementBar
          announcement={announcement}
          primaryColor={primaryColor}
          fontFamily={fontFamily}
          orgId={orgId}
          selectable={selectable}
          selectedSectionId={selectedSectionId}
          onSectionClick={onSectionClick}
        />
        <div
          role={selectable ? "button" : undefined}
          tabIndex={selectable ? 0 : undefined}
          onClick={selectable ? (e) => onSectionClick?.("__nav__", e) : undefined}
          onKeyDown={
            selectable
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") onSectionClick?.("__nav__", e as unknown as React.MouseEvent)
                }
              : undefined
          }
          style={{
            position: "relative",
            background: "#fff",
            padding: "10px 20px",
            borderBottom: "1px solid #eee",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: selectable ? "pointer" : undefined,
            outline: selectedSectionId === "__nav__" ? `2px solid ${primaryColor}` : "none",
            outlineOffset: -2,
            transition: "outline 0.15s",
          }}
        >
          {selectedSectionId === "__nav__" && (
            <div
              style={{
                position: "absolute",
                top: 10,
                left: 10,
                padding: "3px 8px",
                background: primaryColor,
                color: "#fff",
                fontSize: 9,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                borderRadius: 5,
                zIndex: 10,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Nav
            </div>
          )}
          {navBarContent}
        </div>
        {isMobile && drawerContent}
      </div>
      {orgId && (
        <DonationModal
          isOpen={donationModalOpen}
          onClose={() => setDonationModalOpen(false)}
          orgId={orgId}
          orgName={orgName}
          primaryColor={primaryColor}
        />
      )}
    </>
  )
}
